import { RGBAColor } from 'deck.gl';
import { Moment } from 'moment';
import { MarkerItem } from '../types/home';
import { DownloadOption, IDatasetBrief } from '../types/metadata';
import { DateFormat, DTFormat } from './StaticVariable';

export function createEmptyGeoJson(): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: [],
  };
}

export function openInNewTab(url: string) {
  return window.open(url, '_blank', 'noopener,noreferrer');
}

export const GLOBAL_HD_DTU_ID = 'waterdata-dataset-Global_HD_DTU';

export const MIKE_DATE_FORMAT = 'DD MMM yyyy';
export const MIKE_DATETIME_FORMAT = 'DD MMM yyyy h:mm';

export function nicelyFormatDate(date: Moment, includeTime = false) {
  return includeTime
    ? date.format(MIKE_DATETIME_FORMAT)
    : date.format(MIKE_DATE_FORMAT);
}

export function formatDate(date: Moment, includeTime = false) {
  return includeTime ? date.format(DTFormat) : date.format(DateFormat);
}

export function formatMonth(month: number) {
  let formattedMonth = 'All month';
  switch (month) {
    case 1:
      formattedMonth = 'Jan';
      break;
    case 2:
      formattedMonth = 'Feb';
      break;
    case 3:
      formattedMonth = 'Mar';
      break;
    case 4:
      formattedMonth = 'Apr';
      break;
    case 5:
      formattedMonth = 'May';
      break;
    case 6:
      formattedMonth = 'Jun';
      break;
    case 7:
      formattedMonth = 'Jul';
      break;
    case 8:
      formattedMonth = 'Aug';
      break;
    case 9:
      formattedMonth = 'Sep';
      break;
    case 10:
      formattedMonth = 'Oct';
      break;
    case 11:
      formattedMonth = 'Nov';
      break;
    case 12:
      formattedMonth = 'Dec';
      break;
    default:
      break;
  }
  return formattedMonth;
}

export function ConvertHexToRGBA(hex: string, transparent?: number) {
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  if (transparent !== undefined) return [r, g, b, transparent] as RGBAColor;
  else return [r, g, b] as RGBAColor;
}

export function AddTransparencyToHexColor(hex: string, transparency: string) {
  const extract = hex.replace('#', '');
  return '#' + extract + transparency;
}

function ColorToHex(color: number) {
  const hexadecimal = color.toString(16);
  return hexadecimal.length == 1 ? '0' + hexadecimal : hexadecimal;
}

export function ConvertRGBtoHex(rgbaColor: RGBAColor) {
  return (
    '#' +
    ColorToHex(rgbaColor[0]) +
    ColorToHex(rgbaColor[1]) +
    ColorToHex(rgbaColor[2])
  );
}

export const ITEM = {
  VALIDATION: 'validation',
  ANALYTICS: 'analytics',
  REPORTS: 'reports',
} as const;

export type TabKey = typeof ITEM[keyof typeof ITEM];

export enum MethodWay {
  BC = 'BC',
  PP = 'PP',
  INV = 'INV',
}

export enum DownloadCheckboxKey {
  SW = 'SW',
  HD = 'HD',
  Wind = 'Wind',
}

export const datasetType: { [key: string]: string } = {
  SW: 'Waves',
  HD: 'Ocean',
  Wind: 'Atmosphere',
};

export const selectedColor = '#00A4EC';

export const DatasetColors = ['#19506E'];

export const THOWDWAVESPECTRA = '2D Wave Spectra';

export const analyticsDefinitionSorted = [
  'Time Series Plot',
  'Rose Plot',
  'Scatter Plot',
  'Scatter Table',
  'Statistics',
  'Tidal Level',
  'Exceedance',
  'Histogram (w. Weibull Parameters)',
  'Persistence (Weather windows)',
  'Extreme Values',
];

export const SCATTER_PLOT_VALIDATION = 0;
export const TS_PLOT_VALIDATION = 1;
export const ROSE_PLOT_VALIDATION = 2;

export const LegendColors = [
  '#FBE626',
  '#DEE130',
  '#B1D941',
  '#88D150',
  '#5DC861',
  '#4AB76E',
  '#3BA979',
  '#279787',
  '#23888D',
  '#2A798C',
  '#31698C',
  '#38598B',
  '#3C4A84',
  '#3F3876',
  '#412768',
  '#450E55',
];

export function openEULAPdf() {
  window.open(
    'https://www.metocean-on-demand.com/fileshare/getfile.ashx?type=license&file_name=DHI_Metocean_Data_Portal_EULA.pdf',
    '_blank'
  );
}

export function openPrivatePolicy() {
  window.open('https://www.dhigroup.com/privacy', '_blank');
}

export const SEARCH_PARAM = {
  COOR: 'coor',
  FWEPOINTNAME: 'fwePointName',
  SW: 'SW',
  HD: 'HD',
  WIND: 'Wind',
  ACTIVE_PANEL: 'activePanel',
};

export const downloadOptionsParameters: DownloadOption[] = [
  { label: '.csv', value: 'csv' },
  { label: '.mat', value: 'mat' },
  { label: '.nc', value: 'nc' },
  { label: '.dfs0', value: 'dfs0' },
];

export const downloadOptions2DSpectra: DownloadOption[] = [
  { label: '.nc', value: 'nc' },
  { label: '.mat', value: 'mat' },
  { label: '.dfs2', value: 'dfs2' },
];

export const randomColor: RGBAColor = [
  Math.floor(Math.random() * 256),
  Math.floor(Math.random() * 256),
  Math.floor(Math.random() * 256),
];

export function getPointNameAndAcronym(
  datasetId: string,
  briefs: IDatasetBrief[],
  activePoint?: MarkerItem,
  datasetY?: string
) {
  const brief = briefs.find((b: IDatasetBrief) => b.id === datasetId);
  const pointName =
    activePoint && activePoint.label ? activePoint.label + '_' : '';
  const acronym = brief !== undefined ? brief.acronym + '_' : '';
  const briefY = datasetY
    ? briefs.find((b: IDatasetBrief) => b.id === datasetY)
    : undefined;
  const acronymY = briefY !== undefined ? briefY.acronym + '_' : '';
  return pointName + acronym + acronymY;
}
